import type { Staff } from "@/lib/authv2/store";
import { ACTION } from "@/lib/auth/actions";
import { hasPermission } from "@/lib/authv2/permissions";

// NOTE: Department-driven predicates (isRetouchLeader, isRetoucher,
// canEditRetouchGrid, canEditRetouchLinks) have no clean BE action mapping
// during Model A migration — kept on role/dept check. Revisit if BE adds
// retouch-dept-aware policy.

// vai Điều phối khoá 23/08/2026 (quyết định 0050)
const FULL_ACCESS = ["admin", "director", "manager"];

/**
 * Hình dạng tối thiểu để trả lời câu hỏi "người này thuộc phía nào": chỉ cần
 * vai và phòng ban. Trang Đơn hàng cầm `UserForTransitions` chứ không cầm
 * `Staff` đầy đủ, mà câu hỏi thì y hệt — nhận hình dạng hẹp này để hai màn
 * dùng CHUNG một câu trả lời, thay vì chép lại vế so sánh rồi trôi khỏi nhau.
 */
export type RoleAndDept = { role?: string | null; department?: string | null };

function role(s: RoleAndDept | null): string {
  return s?.role?.toLowerCase() ?? "";
}

function dept(s: RoleAndDept | null): string {
  return s?.department?.toLowerCase() ?? "";
}

/** Leader = manage retouch flow (assign, approve, reject). QC phòng retouch = leader (spec 2026-08-23 §5.7). */
export function isRetouchLeader(s: Staff | null): boolean {
  if (!s) return false;
  if (FULL_ACCESS.includes(role(s))) return true;
  return (role(s) === "manager" || role(s) === "qc") && dept(s) === "retouch";
}

/**
 * Retouch dept staff (incl. leader).
 *
 * Gương của IsRetouchSideActor ở backend/internal/core/order/transitions.go.
 * Nhận `RoleAndDept` chứ không riêng `Staff`: trang Đơn hàng cũng hỏi đúng câu
 * này để ẩn nút "Sửa xong" trên đơn có bước hậu kỳ (CEO chốt 1a 16/09/2026).
 */
export function isRetoucher(s: RoleAndDept | null): boolean {
  if (!s) return false;
  // vai Điều phối khoá 23/08/2026 (quyết định 0050)
  if (["admin", "director"].includes(role(s))) return true;
  return dept(s) === "retouch";
}

/**
 * Đổi HẠN TRẢ ẢNH của một đơn (CEO chốt 3a+b ngày 25/09/2026).
 *
 * Gương của cskh.MayEditDeadline ở backend/internal/core/cskh/service.go.
 * Hai bên phải trả lời giống nhau: lệch một chiều là nút chết (bấm ăn 403),
 * lệch chiều kia là nút hiện cho người máy chủ sẽ từ chối.
 *
 * Nhận cờ `coCskhWrite` từ ngoài thay vì tự hỏi kho quyền: hàm này thuần để
 * bài kiểm gọi thẳng được, còn màn hình truyền `hasPermission(ACTION.CskhWrite)`.
 *
 * Điểm chính: THỢ hậu kỳ KHÔNG đổi được hạn. Hạn là lời hứa với khách — để
 * người đang làm tự nới hạn cho mình là bỏ mất chỗ duy nhất có người phải cân
 * nhắc trước khi lùi hẹn.
 */
export function canEditRetouchDeadline(s: Staff | null, coCskhWrite: boolean): boolean {
  if (coCskhWrite) return true;
  if (!s) return false;
  if (["admin", "director"].includes(role(s))) return true;
  return (role(s) === "manager" || role(s) === "qc") && dept(s) === "retouch";
}

/** Edit retouch_work payroll grid. Gương BE: retouch.write + cổng qcRetouchOnly. */
export function canEditRetouchGrid(s: Staff | null): boolean {
  if (FULL_ACCESS.includes(role(s))) return true;
  return role(s) === "qc" && dept(s) === "retouch";
}

/**
 * Gõ số ảnh hậu kỳ ở thẻ "🖼️ Hậu kỳ" trang lương. Gương BE mayKeyRetouchDesk
 * (CEO chốt 15/09/2026, câu 2a): quản trị, giám đốc; trưởng phòng và QC CHỈ
 * của phòng hậu kỳ. Hẹp hơn canEditRetouchGrid — hàm đó vẫn gác việc thẻ có
 * hiện hay không, nên trưởng phòng phòng khác vẫn thấy thẻ, chỉ không có ô gõ.
 * Dòng của chính mình khoá riêng ở màn gõ (gương mustNotKeyOwnRow).
 */
export function canKeyRetouchDesk(s: Staff | null): boolean {
  if (!s) return false;
  if (["admin", "director"].includes(role(s))) return true;
  return (role(s) === "manager" || role(s) === "qc") && dept(s) === "retouch";
}

// Bốn nút chuyển trạng thái hậu kỳ (Gửi duyệt, Duyệt, Yêu cầu sửa, Hoàn tất —
// kể cả "Hoàn thành retouch" ở trang đơn). Gương BE: middleware lọc VAI bằng
// retouch.complete / retouch.review, rồi retouchDeptOnly lọc PHÒNG BAN (CEO
// chốt 15/09/2026). Hai quyền đó cấp cho nhân viên và trưởng phòng mọi phòng,
// nên ở đây phải kiểm CẢ HAI — không thì nút hiện ra cho trưởng phòng CSKH rồi
// bấm vào ăn 403 (câu 4a: ẩn luôn, như nút "Nhận việc" bên dưới).

/** Mark order's retouch as complete. */
export function canCompleteRetouch(s: Staff | null): boolean {
  if (!s) return false;
  return hasPermission(ACTION.RetouchComplete) && isRetoucher(s);
}

/** Submit final link for review. */
export function canSubmitFinal(s: Staff | null): boolean {
  if (!s) return false;
  return hasPermission(ACTION.RetouchComplete) && isRetoucher(s);
}

/** Approve / reject final link — leader only. */
export function canReviewFinal(s: Staff | null): boolean {
  if (!s) return false;
  return hasPermission(ACTION.RetouchReview) && isRetoucher(s);
}

/**
 * Ô chấm điểm chất lượng ảnh 0–10 ở bản cuối. Ô này lưu qua cửa sửa đơn
 * (PUT /orders/:id, trường satisfaction), mà cửa đó mở cho mọi người có
 * order.write cộng phòng lễ tân và marketing — rộng hơn hẳn ô trên màn.
 *
 * CEO chốt câu 2a 16/09/2026: rào về phòng hậu kỳ + giám đốc + quản trị. Gương
 * BE là IsRetouchSideActor ở backend/internal/core/order/transitions.go, cửa
 * gác đặt ở handler update. Hai đầu phải trả lời GIỐNG NHAU: lệch một chiều là
 * ô hiện ra rồi gõ vào ăn 403 (ô chết), lệch chiều kia là giao diện giấu mà máy
 * chủ vẫn nhận (lỗ hổng). Sửa một bên thì sửa cả hai, cùng commit.
 */
export function canScoreRetouch(s: Staff | null): boolean {
  if (!s) return false;
  return hasPermission(ACTION.RetouchReview) && isRetoucher(s);
}

/** Assign retoucher — leader only. */
export function canAssignRetoucher(s: Staff | null): boolean {
  if (!s) return false;
  return hasPermission(ACTION.RetouchReview);
}

/**
 * Tự nhận việc trong kho chung (CEO chốt 6a). Gương BE: middleware lọc VAI
 * bằng retouch.complete, service lọc PHÒNG BAN bằng mayClaimRetouch — nên ở
 * đây phải kiểm CẢ HAI, không thì nút hiện ra cho lễ tân rồi bấm vào ăn 403.
 */
export function canClaimRetouchOrder(s: Staff | null): boolean {
  if (!s) return false;
  return hasPermission(ACTION.RetouchComplete) && isRetoucher(s);
}

/** Edit photo / final links — retouchers + leaders. */
export function canEditRetouchLinks(s: Staff | null): boolean {
  return isRetoucher(s);
}

/**
 * Mở được "Bàn làm việc" của một đơn Album không (QĐ 0361)? Bàn đọc kho ảnh
 * (`GET /orders/{id}/gallery/fix-board`), mà kho ảnh chỉ mở album cho thợ CÓ
 * TÊN trên đơn (gương `authorizeGalleryRead` ở backend/internal/core/gallery/
 * upload_repo.go). Lệch là thợ chưa đứng tên bấm dòng rồi ăn trang "không có
 * quyền" — trả false thì màn xổ khung chi tiết như đơn thường.
 *
 * - Đứng tên trên đơn: so `retouchers[].id` với mã nhân sự (`staff.id`, cùng
 *   loại với mảng ê-kíp). Dòng chỉ có tên mà thiếu mã KHÔNG tính: kho ảnh so
 *   theo mã.
 * - Người giao việc (`canAssignRetoucher`) — trừ QC: kho ảnh xếp QC chung luật
 *   thợ cùng phòng (chỉ đơn có tên mình).
 */
export function canOpenRetouchWorkbench(
  s: Staff | null,
  retouchers: readonly { id?: string | null }[] | null | undefined,
): boolean {
  if (!s) return false;
  if (s.id && (retouchers ?? []).some((m) => m.id === s.id)) return true;
  return canAssignRetoucher(s) && role(s) !== "qc";
}
